import { PurchaseDto, FoodDto } from "../../api";
import { Purchase } from "../../data/purchase";
import { FoodUnit } from "../../data/foodUnit";
import {
  MealIngredient,
  MealWithIngredients,
} from "../../data/mealWithIngredients";
import { PlanCell } from "../../data/plan";
import {
  CookingOverrides,
  findById,
  getMIKey,
  isNullIndex,
} from "../../utils/planUtils";
import {
  convertByNetWeight,
  formatPurchaseDescription,
  selectPurchaseUnit,
} from "../../utils/purchaseUtils";
import { ConsumeRow } from "./PlannedConsumeCard";

export const PLAN_VALUE = "__plan__";

export type IngredientEdit = {
  amount?: number;
  unitId?: number;
  purchaseId?: number | null;
  /** Zutat im Verzehr entfernt (erscheint dann als „wieder aufnehmen"). */
  deleted?: boolean;
};

export type PlannedEdit = {
  checked: boolean;
  ingredients: Record<string, IngredientEdit>;
};

export type IngredientMeta = {
  key: string;
  name: string;
  isFoodBased: boolean;
  plannedAmount: number;
  planUnitId: number;
  planPurchaseId: number | null;
  defaultPurchaseId: number | null;
  planUnits: FoodUnit[];
  purchases: Purchase[];
};

export type PlannedBase = {
  sig: string;
  personId: number;
  personName?: string;
  meal: MealWithIngredients;
};

export type ConsumedCell = {
  sig: string;
  personId: number;
  personName?: string;
  index: number;
  cell: PlanCell;
};

export function buildMeta(
  meal: MealWithIngredients,
  foods: FoodDto[] | undefined,
  purchases: PurchaseDto[] | undefined,
): IngredientMeta[] {
  const result: IngredientMeta[] = [];
  for (const mi of meal.mealIngredients) {
    const isFoodBased = !isNullIndex(mi.foodUnitFoodId);
    if (isFoodBased) {
      const food = findById(foods ?? [], mi.foodUnitFoodId!);
      const planUnits = (food?.foodUnits ?? []) as FoodUnit[];
      const foodPurchases = (purchases ?? []).filter(
        (p) => p.foodId === mi.foodUnitFoodId,
      );
      result.push({
        key: String(mi.id),
        name: food?.description || mi.foodUnitFoodDescription || "",
        isFoodBased,
        plannedAmount: mi.amount,
        planUnitId: mi.foodUnitId!,
        planPurchaseId: null,
        defaultPurchaseId: food?.defaultPurchaseId ?? null,
        planUnits,
        purchases: foodPurchases,
      });
    } else {
      const purchase = findById(purchases ?? [], mi.purchaseUnitPurchaseId!);
      const foodId = purchase?.foodId;
      const planUnits = (purchase?.purchaseUnits ?? []).map((pu) => ({
        id: pu.id,
        description: pu.description,
        netWeight: pu.netWeight,
        isDefault: pu.isDefault,
      }));
      const foodPurchases = (purchases ?? []).filter(
        (p) => p.foodId === foodId,
      );
      result.push({
        key: String(mi.id),
        name:
          formatPurchaseDescription(purchase) ||
          mi.purchaseUnitPurchaseDescription ||
          "",
        isFoodBased,
        plannedAmount: mi.amount,
        planUnitId: mi.purchaseUnitId!,
        planPurchaseId: mi.purchaseUnitPurchaseId ?? null,
        defaultPurchaseId: null,
        planUnits,
        purchases: foodPurchases,
      });
    }
  }
  return result;
}

export function resolveRowView(
  meta: IngredientMeta,
  edit: IngredientEdit | undefined,
  purchases: PurchaseDto[] | undefined,
): ConsumeRow {
  let units = meta.planUnits;
  let purchaseId: number | null | undefined = edit?.purchaseId;
  if (purchaseId === undefined || purchaseId === null) {
    if (!meta.isFoodBased && purchaseId === undefined) {
      purchaseId = meta.planPurchaseId;
    } else {
      purchaseId = null;
    }
  }
  if (purchaseId != null && purchaseId !== meta.planPurchaseId) {
    const purchase = findById(purchases ?? [], purchaseId);
    units = (purchase?.purchaseUnits ?? []).map((pu) => ({
      id: pu.id,
      description: pu.description,
      netWeight: pu.netWeight,
      isDefault: pu.isDefault,
    }));
  }
  const unitId = edit?.unitId ?? meta.planUnitId;
  const currentUnit =
    units.find((u) => u.id === unitId) ??
    units.find((u) => u.id === meta.planUnitId) ??
    units[0];

  // Grün markierter Status, sobald ein Produkt explizit (aus)gewählt wurde
  let status: string | undefined;
  let nameOverride: string | undefined;
  const chosenPurchase = edit?.purchaseId;
  if (edit && chosenPurchase !== undefined) {
    if (chosenPurchase != null && chosenPurchase !== meta.planPurchaseId) {
      status =
        formatPurchaseDescription(findById(purchases ?? [], chosenPurchase)) ||
        meta.name;
      nameOverride = status;
    } else if (!meta.isFoodBased) {
      status =
        formatPurchaseDescription(
          findById(purchases ?? [], meta.planPurchaseId ?? -1),
        ) || meta.name;
      nameOverride = status;
    } else {
      status = "Planwert beibehalten";
    }
  }

  // Gold markiert, solange es (nicht-Default-)Produkte zur Auswahl gibt –
  // unabhängig davon, ob bereits eines gewählt wurde.
  const highlighted = (purchases ?? []).some((p) => !p.isDefault);

  return {
    highlighted,
    key: meta.key,
    name: nameOverride ?? meta.name,
    status,
    units,
    currentUnit,
    amount: edit?.amount ?? meta.plannedAmount,
  };
}

/** Die rein aus der MealIngredient ableitbaren Planwerte (ohne Lookups). */
export type PlanningValues = Pick<
  IngredientMeta,
  "plannedAmount" | "planUnitId" | "planPurchaseId"
>;

export function planMetaFromIngredient(mi: MealIngredient): PlanningValues {
  const isFoodBased = !isNullIndex(mi.foodUnitFoodId);
  return {
    plannedAmount: mi.amount,
    planUnitId: (isFoodBased ? mi.foodUnitId : mi.purchaseUnitId)!,
    planPurchaseId: isFoodBased ? null : mi.purchaseUnitPurchaseId ?? null,
  };
}

/** Prüft, ob ein Zutaten-Edit von den Planwerten abweicht. */
export function isIngredientCustom(
  edit: IngredientEdit | undefined,
  plan: PlanningValues,
): boolean {
  return (
    !!edit &&
    (!!edit.deleted ||
      (edit.amount !== undefined && edit.amount !== plan.plannedAmount) ||
      (edit.unitId !== undefined && edit.unitId !== plan.planUnitId) ||
      (edit.purchaseId !== undefined &&
        edit.purchaseId !== plan.planPurchaseId))
  );
}

/**
 * Übersetzt die Live-Edits einer geplanten Mahlzeit in die `CookingOverrides`,
 * die `createCustomIntakes` beim Verzehr erwartet. Gelöschte Zutaten werden
 * explizit mit `deleted: true` markiert – sonst würde `createCustomIntakes` sie
 * als unverändert übernehmen und der Verzehr (inkl. Nährwerten) wäre zu hoch.
 */
export function buildConsumeOverrides(
  meal: MealWithIngredients,
  edit: PlannedEdit,
  personId: number,
): CookingOverrides {
  const overrides: CookingOverrides = {};
  meal.mealIngredients.forEach((mi) => {
    const ing = edit.ingredients[String(mi.id)];
    const plan = planMetaFromIngredient(mi);
    if (!isIngredientCustom(ing, plan)) return;
    const isFoodBased = !isNullIndex(mi.foodUnitFoodId);
    const purchaseId = ing?.purchaseId ?? plan.planPurchaseId;
    overrides[getMIKey(mi)] = {
      personAmounts: {
        [personId]: ing?.amount ?? plan.plannedAmount,
      },
      unitId: ing?.unitId ?? plan.planUnitId,
      purchaseId: isFoodBased ? purchaseId ?? null : purchaseId,
      deleted: !!ing?.deleted,
    };
  });
  return overrides;
}

/**
 * Berechnet den neuen Zutaten-Edit bei einer Produktwahl. `purchases` sind alle
 * Produkte des Foods der Zutat (`usePurchasesByFood`).
 */
export function productEditFor(
  meta: IngredientMeta,
  cur: IngredientEdit | undefined,
  value: string,
  purchases: PurchaseDto[],
  /** Bei food-based: die aktuell gewählte (ggf. alternative) FoodUnit. */
  activeFoodUnitId?: number,
): IngredientEdit {
  const prev = cur ?? {};
  const view = resolveRowView(meta, prev, purchases);
  const oldAmount = prev.amount ?? meta.plannedAmount;
  const oldNW = view.currentUnit?.netWeight ?? 1;
  const oldUnitDescription = view.currentUnit?.description;

  if (value === PLAN_VALUE) {
    if (meta.isFoodBased) {
      // Aktives Food (Alternative) NICHT zurücksetzen.
      const targetId = activeFoodUnitId ?? meta.planUnitId;
      const unit =
        meta.planUnits.find((u) => u.id === targetId) ?? meta.planUnits[0];
      return {
        amount: convertByNetWeight(oldAmount, oldNW, unit?.netWeight),
        unitId: unit?.id ?? targetId,
        purchaseId: null,
      };
    }
    const planUnit = meta.planUnits.find((u) => u.id === meta.planUnitId);
    return {
      amount: convertByNetWeight(oldAmount, oldNW, planUnit?.netWeight),
      unitId: planUnit?.id ?? meta.planUnitId,
      purchaseId: meta.planPurchaseId,
    };
  }

  const purchase = findById(purchases, Number(value));
  if (!purchase || purchase.purchaseUnits.length === 0) return prev;
  let targetFoodUnitId: number | undefined;
  if (meta.isFoodBased) {
    targetFoodUnitId = view.currentUnit?.id ?? meta.planUnitId;
  } else {
    const prevPurchaseId = prev.purchaseId ?? meta.planPurchaseId;
    if (prevPurchaseId && prevPurchaseId !== purchase.id) {
      const prevPurchase = findById(purchases, prevPurchaseId);
      targetFoodUnitId = prevPurchase?.purchaseUnits?.find(
        (pu) => pu.id === view.currentUnit?.id,
      )?.foodUnitId;
    }
  }
  const newPU = selectPurchaseUnit(purchase, {
    foodUnitId: targetFoodUnitId,
    oldNetWeight: oldNW,
    oldUnitDescription,
  });
  return {
    amount: convertByNetWeight(oldAmount, oldNW, newPU?.netWeight),
    unitId: newPU?.id,
    purchaseId: purchase.id,
  };
}
